import React, { useState, useEffect, useRef } from 'react';

import OptimizedImage from './OptimizedImage';

import popupBg from 'url:../../assets-optimized/redesign/download_popup.jpg';
import popupBgWebp from 'url:../../assets-optimized/redesign/download_popup.webp';
import popupBgAvif from 'url:../../assets-optimized/redesign/download_popup.avif';

import popupBgMobile from 'url:../../assets-optimized/redesign/download_popup_mobile.png';
import popupBgMobileWebp from 'url:../../assets-optimized/redesign/download_popup_mobile.webp';
import popupBgMobileAvif from 'url:../../assets-optimized/redesign/download_popup_mobile.avif';

import reportPartOne from 'url:../../assets/redesign/tapping-into-the-psychology-of-travelers-part-i-marriott-media-x-kinective-media.pdf';
import reportPartTwo from 'url:../../assets/redesign/tapping-into-the-psychology-of-travelers-part-ii-marriott-media-x-kinective-media.pdf';

import './ReportModal.css';

const CloseIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M6 6L18 18M18 6L6 18' stroke='#171717' strokeWidth='2' strokeLinecap='round' />
  </svg>
);

const DownloadIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 4V15M12 15L7.5 10.5M12 15L16.5 10.5'
      stroke='#fff'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path d='M5 19H19' stroke='#fff' strokeWidth='2' strokeLinecap='round' />
  </svg>
);

const CheckIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M5 12.5L10 17.5L19 7'
      stroke='#171717'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const installments = [
  {
    id: 'one',
    blurb: 'Exploring the consumer mindset through the journey.',
    title: 'Download Installment One',
    file: reportPartOne,
    fileName:
      'tapping-into-the-psychology-of-travelers-part-i-marriott-media-x-kinective-media.pdf',
  },
  {
    id: 'two',
    blurb: 'Turning mindset into measurable impact',
    title: 'Download Installment Two',
    file: reportPartTwo,
    fileName:
      'tapping-into-the-psychology-of-travelers-part-ii-marriott-media-x-kinective-media.pdf',
  },
];

const ReportModal = ({ isOpen, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [render, setRender] = useState(isOpen);
  const [downloaded, setDownloaded] = useState({});
  const modalRef = useRef(null);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setRender(true);
      setIsClosing(false);
      // Reset the per-card confirmation each time the dialog opens.
      setDownloaded({});
    } else if (render) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setRender(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, render]);

  useEffect(() => {
    if (!render || !isOpen || !modalRef.current) return undefined;

    const focusable = [
      ...modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
    ];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [render, isOpen]);

  const handleDownload = (item) => {
    if (item.file) {
      const link = document.createElement('a');
      link.href = item.file;
      link.download = item.fileName || '';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    setDownloaded((prev) => ({ ...prev, [item.id]: true }));
  };

  if (!render) return null;

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('report-modal-overlay')) {
      handleClose();
    }
  };

  return (
    <div
      className={`report-modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={`report-modal ${isClosing ? 'closing' : ''}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby='report-modal-title'
      >
        <OptimizedImage
          avif={popupBgAvif}
          webp={popupBgWebp}
          fallback={popupBg}
          mobileAvif={popupBgMobileAvif}
          mobileWebp={popupBgMobileWebp}
          mobileFallback={popupBgMobile}
          mobileMedia='(max-width: 600px)'
          alt=''
          className='report-modal-bg'
        />

        <button className='report-modal-close' onClick={handleClose} aria-label='Close dialog'>
          <CloseIcon />
        </button>

        <p id='report-modal-title' className='report-modal-intro'>
          Download the latest research on the psychology of travel and the consumer mindset brought
          to you by Marriott Media and Kinective Media by United Airlines.
        </p>

        <div className='report-modal-cards'>
          {installments.map((item) => {
            const done = downloaded[item.id];
            return (
              <button
                key={item.id}
                type='button'
                className={`report-card ${done ? 'is-done' : ''}`}
                onClick={() => !done && handleDownload(item)}
                disabled={done}
              >
                <span className='report-card-body'>
                  <span className='report-card-blurb'>
                    {done ? 'Thanks for your interest.' : item.blurb}
                  </span>
                  <span className='report-card-btn'>{done ? <CheckIcon /> : <DownloadIcon />}</span>
                  <span className='report-card-title'>
                    {done ? 'YOUR DOWNLOAD WILL START SOON.' : item.title}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
